import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useBasket } from "../contexts/BasketContext";

const nav = [
  { to: "/ubytovani", label: "Ubytování" },
  { to: "/zazitky/rizena-degustace", label: "Degustace" },
  { to: "/zazitky", label: "Zážitky" },
  { to: "/vina", label: "Naše vína" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  const { totalBottles, setOpen } = useBasket();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const isHome = loc.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  return (
    <>
      <a href="#main" className="skip-link">Přeskočit na obsah</a>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
          transparent ? "bg-transparent" : "bg-ivory/95 backdrop-blur border-b border-black/5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2">
            <span className={`font-serif text-xl md:text-2xl tracking-tight ${transparent ? "text-ivory" : "text-charcoal"}`}>
              Vinařství Brychta
            </span>
            <span className={`hidden md:inline text-[10px] tracking-[.2em] uppercase ${transparent ? "text-ivory/70" : "text-charcoal/60"}`}>
              Charvátská Nová Ves
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Hlavní menu">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\W+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    transparent ? "text-ivory hover:text-gold" : "text-charcoal hover:text-burgundy"
                  } ${isActive ? "text-burgundy" : ""}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="basket-open-btn"
              className={`relative hidden md:flex items-center gap-2 text-sm px-3 py-2 rounded-sm border transition-colors ${
                transparent ? "border-ivory/40 text-ivory hover:bg-ivory/10" : "border-charcoal/20 text-charcoal hover:bg-cream"
              }`}
              aria-label="Otevřít poptávku vína"
            >
              <ShoppingBag size={16} />
              <span className="hidden xl:inline">Poptávka vína</span>
              {totalBottles > 0 && (
                <span data-testid="basket-count" className="min-w-[20px] h-5 px-1 rounded-full bg-burgundy text-ivory text-[11px] flex items-center justify-center">
                  {totalBottles}
                </span>
              )}
            </button>

            <Link
              to="/kontakt?type=ubytovani#poptavka"
              data-testid="header-reserve-cta"
              className="hidden md:inline-flex bg-burgundy hover:bg-burgundy-hover text-ivory text-sm px-5 py-2.5 rounded-sm tracking-wide transition-colors"
            >
              Poptat ubytování
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className={`lg:hidden p-2 -mr-2 ${transparent ? "text-ivory" : "text-charcoal"}`}
              aria-label={menuOpen ? "Zavřít menu" : "Otevřít menu"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-30 lg:hidden bg-ivory pt-20 overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-1" aria-label="Mobilní menu">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace(/\W+/g, "-")}`}
                className="py-4 border-b border-charcoal/10 font-serif text-2xl text-charcoal"
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt?type=ubytovani#poptavka"
              data-testid="mobile-reserve-cta"
              className="mt-6 bg-burgundy text-ivory text-center py-4 rounded-sm tracking-wide"
            >
              Poptat ubytování
            </Link>
            <button
              type="button"
              onClick={() => { setMenuOpen(false); setOpen(true); }}
              className="mt-3 border border-charcoal/25 text-charcoal py-4 rounded-sm tracking-wide"
              data-testid="mobile-basket-btn"
            >
              Poptávka vína {totalBottles > 0 ? `(${totalBottles})` : ""}
            </button>
          </nav>
        </div>
      )}
    </>
  );
}
